import type { ReactElement } from "react";
import { Box } from "@mui/material";

import Loader from "./Loader/Loader";
import NotFound from "../pages/NotFound";
import { DEFAULT_SUPER_ADMIN_ROLE_ID } from "../utils/constants";
import { RootState, useAppSelector } from "../store";

/**
 * Route guard that only renders its children for super-admin users.
 *
 * This is a UX-only gate — the backend is the real enforcement point and
 * returns 403 for non-super-admin callers. Direct URL navigation by a
 * non-super-admin renders the NotFound page instead of the guarded content.
 *
 * userDetails is not persisted across reloads (see authReducer initialState),
 * so on a hard refresh it is undefined until BasicLayout's getUserByMail call
 * resolves. Treating that gap as "not a super admin" would flash NotFound at a
 * legitimate user, so wait for the profile while a session is present.
 */
const RequireSuperAdmin = ({ children }: { children: ReactElement }) => {
  const { userDetails, userMail } = useAppSelector(
    (state: RootState) => state.auth
  );

  if (!userDetails) {
    // A stored userMail means a session exists and the profile is still in
    // flight; with no session there is nothing to wait for.
    if (!userMail) {
      return <NotFound />;
    }
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <Loader />
      </Box>
    );
  }

  if (userDetails.user_role_id !== DEFAULT_SUPER_ADMIN_ROLE_ID) {
    return <NotFound />;
  }

  return children;
};

export default RequireSuperAdmin;
